import {
  Box,
  Chip,
  Dialog,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useEffect, useState } from "react";
import {
  InventoryData,
  HistoryData,
  Status,
} from "../../../../types/inventoryData";
import {
  GetAllHistoriesFromProduct,
  UpdateProduct,
} from "../../../../api/inventory";
import ProductTimeline from "../ProductTimeline";
import { CheckRounded, Edit, QrCodeRounded } from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { useInventory } from "../../InventoryProvider";
import { getProductStatusColor } from "../../../../helpers/utils";
import { productStatusOptions } from "../../../../helpers/selectOptions";
import { ErrorCodes } from "../../../../types/enums";
import { t } from "i18next";
import { useApplicationContext } from "../../../ContextProviders/ApplicationContext";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { inventoryRules } from "../../../../helpers/validationSchemes";
import { useTranslation } from "react-i18next";

type productTypes = {
  open: boolean;
  handleClose: () => void;
};

type ToEditElementTypes = {
  children: any;
  field: string;
};

type EditingElementTypes = {
  children: any;
  field: string;
  handleCancel: (field: string) => void;
};

type ElementTypes = {
  type: string;
  // body: any;
};

const ProductPopup: React.FC<productTypes> = ({ open, handleClose }) => {
  const {
    selectedProduct,
    setSelectedProduct,
    updateSingleRow,
    plans,
    addBatchNumberAndActions,
    fetchPlans,
    isPhoneSize,
    headCells,
  } = useInventory();
  const theme = useTheme();
  const { showSnackbar } = useApplicationContext();
  const { t } = useTranslation();
  const [lastmodified, setLastModified] = useState<string>();
  const [history, setHistory] = useState<HistoryData[]>([]);
  const [editField, setEditField] = useState<string | null>(null);

  const formatDate = (newDate: Date) => {
    const date = new Date(newDate);
    return format(date, "yyyy-MM-dd HH:mm:ss");
  };

  const [createdAt, setCreatedAt] = useState<string>(
    selectedProduct ? formatDate(selectedProduct.createdAt) : ""
  );

  if (selectedProduct == undefined) return <></>;

  const style = {
    // position: "absolute" as "absolute",
    // top: "50%",
    // left: "50%",
    // transform: "translate(-50%, -50%)",
    // width: "50%",
    // height: "90%",
    // bgcolor: "background.paper",
    // border: "none",
    boxShadow: isPhoneSize ? 0 : 24,
    p: 3,
    // borderRadius: 2,
    display: "flex",
    flexDirection: "column",
    overflow: isPhoneSize ? "none" : "hidden",
    gap: 3,
  };

  const fetchHistory = async () => {
    let histories: HistoryData[] = [];
    if (selectedProduct?.productId != null) {
      try {
        histories = await GetAllHistoriesFromProduct(selectedProduct.productId);
      } catch (error) {
        showSnackbar(t("errors.UnexpectedError"), "error");
      }
    }
    if (histories) {
      setLastModified(formatDate(histories[0].changedAt));
    }
    setHistory(histories);
  };

  useEffect(() => {
    fetchHistory();
  }, [selectedProduct]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      handleSubmit(handleSave);
    }
  };

  const handleSave: SubmitHandler<InventoryData> = async (data) => {
    if (!selectedProduct) return;

    if (isDirty == false) {
      return;
    }

    if (data.plan?.inventoryPlanId == null) data.plan = null;

    try {
      const updatedProduct: InventoryData = await UpdateProduct(data);
      const newUpdatedProduct = addBatchNumberAndActions(updatedProduct);

      // const changedPlanId: InventoryData = {
      //   ...updatedProduct,
      //   status: selectedProduct?.status || Status.Available,
      //   location: selectedProduct?.location || null,
      //   note: selectedProduct?.note || null,
      //   plan: {
      //     inventoryPlanId: selectedProduct?.plan?.inventoryPlanId
      //       ? selectedProduct.plan.inventoryPlanId
      //       : undefined,
      //     endDate: new Date(Date.now()),
      //     startDate: new Date(Date.now()),
      //     allocatedProducts: [],
      //     plannedProducts: [],
      //     companyName: ""
      //   },
      // };

      updateSingleRow(newUpdatedProduct);
      fetchPlans();

      setSelectedProduct(newUpdatedProduct);

      reset(updatedProduct);

      if (newUpdatedProduct.histories) {
        setHistory(newUpdatedProduct.histories);

        const formattedDate = formatDate(
          newUpdatedProduct.histories[0].changedAt
        );
        setLastModified(formattedDate);
      }
      setEditField(null);
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message) {
          case ErrorCodes.ProductNotFound:
            showSnackbar(t("errors.ProductNotFound"), "error");
            break;
          default:
            showSnackbar(t("errors.UnexpectedError"), "error");
            break;
        }
      } else {
        showSnackbar(t("errors.UnexpectedError"), "error");
      }
    }
  };

  const handleCancel = () => {
    reset();
    setEditField(null);
  };

  const handleEdit = (field: string) => {
    setEditField(field);
  };

  const ToEditElement: React.FC<ToEditElementTypes> = ({ children, field }) => {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {children}
        <IconButton onClick={() => handleEdit(field)}>
          <Edit fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const Element: React.FC<ElementTypes> = ({ type }) => {
    // const hasBody = body != null && String(body).trim() !== "";

    const foundHeadCell = headCells.find((h) => h.id == type);
    if (foundHeadCell == undefined) return <></>;

    const value = foundHeadCell.formatValue
      ? foundHeadCell.formatValue(selectedProduct)
      : foundHeadCell.getValue(selectedProduct);

    return (
      <Grid item>
        <Typography variant="body2" color="gray" sx={{ whiteSpace: "nowrap" }}>
          {t(foundHeadCell.label)}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            whiteSpace: "nowrap",
            visibility: foundHeadCell.getValue(selectedProduct)
              ? "visible"
              : "hidden",
          }}
        >
          {value ?? "\u00A0"}
        </Typography>
      </Grid>
    );
  };

  const EditingElement: React.FC<EditingElementTypes> = ({
    children,
    field,
    handleCancel,
  }) => {
    return (
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        {children}
        <IconButton type="submit">
          <CheckRounded />
        </IconButton>
        <IconButton onClick={() => handleCancel(field)}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>
    );
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, errors },
  } = useForm<InventoryData>({
    defaultValues: {
      ...selectedProduct,
      status: selectedProduct?.status || Status.Available,
      location: selectedProduct?.location || null,
      note: selectedProduct?.note || null,
      plan: selectedProduct?.plan
        ? {
            inventoryPlanId: selectedProduct.plan.inventoryPlanId,
          }
        : null,
    },
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableAutoFocus={true}
      maxWidth="md"
      fullWidth
      fullScreen={isPhoneSize}
    >
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ padding: 0 }}>
            {t("inventory.productInformation")}
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Tooltip title={t("inventory.scanNewQrCode")}>
              <IconButton
                onClick={handleClose}
                sx={{
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  "&:hover": { cursor: "pointer" },
                }}
              >
                <QrCodeRounded />
              </IconButton>
            </Tooltip>
            <IconButton
              onClick={handleClose}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                "&:hover": { cursor: "pointer" },
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Box>
        </Box>
        <Grid container rowGap={2}>
          <Grid item xs={6}>
            <Grid container flexDirection="column" gap={2}>
              <Element type="productType" />
              <Element type="size" />
              <Element type="side" />
              <Element type="serial" />
            </Grid>
          </Grid>

          <form onSubmit={handleSubmit(handleSave)} style={{ flexGrow: 1 }}>
            {/* <Grid item xs={6}> */}
            <Grid container flexDirection="column" gap={2}>
              <Grid item>
                <Typography
                  variant="body2"
                  color="gray"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("labels.status")}
                </Typography>

                {editField === "status" ? (
                  <EditingElement field="status" handleCancel={handleCancel}>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="select-status-label"
                          id="select-status"
                        >
                          {productStatusOptions.map((p) => (
                            <MenuItem key={p.value} value={p.value}>
                              {p.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </EditingElement>
                ) : (
                  <ToEditElement field="status">
                    <Chip
                      label={Status[selectedProduct.status]}
                      variant="outlined"
                      size="medium"
                      sx={{
                        color:
                          selectedProduct.status != undefined
                            ? getProductStatusColor(selectedProduct)
                            : "",
                      }}
                    />
                  </ToEditElement>
                )}
              </Grid>

              <Grid item>
                <Typography
                  variant="body2"
                  color="gray"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("inventory.location")}
                </Typography>

                {editField === "location" ? (
                  <EditingElement field="location" handleCancel={handleCancel}>
                    <Controller
                      name="location"
                      control={control}
                      rules={inventoryRules.locationRule}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          hiddenLabel
                          variant="filled"
                          onKeyDown={(event) => handleKeyPress(event)}
                        />
                      )}
                    />
                  </EditingElement>
                ) : (
                  <ToEditElement field="location">
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedProduct.location}
                    </Typography>
                  </ToEditElement>
                )}
              </Grid>

              <Grid item>
                <Typography
                  variant="body2"
                  color="gray"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("inventory.note")}
                </Typography>
                {editField === "note" ? (
                  <EditingElement field="note" handleCancel={handleCancel}>
                    <Controller
                      name="note"
                      control={control}
                      rules={inventoryRules.noteRule}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value || ""}
                          hiddenLabel
                          variant="filled"
                          multiline
                          onKeyDown={(event) => handleKeyPress(event)}
                        />
                      )}
                    />
                  </EditingElement>
                ) : (
                  <ToEditElement field="note">
                    <Typography variant="body1">
                      {selectedProduct.note}
                    </Typography>
                  </ToEditElement>
                )}
              </Grid>

              <Grid item>
                <Typography
                  variant="body2"
                  color="gray"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("inventory.plan")}
                </Typography>

                {editField === "plan" ? (
                  <EditingElement field="plan" handleCancel={handleCancel}>
                    <Controller
                      name="plan.inventoryPlanId"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="select-plan-label"
                          id="select-plan"
                          value={field.value ?? ""}
                          onChange={(e) => {
                            const val =
                              e.target.value == "" ? null : e.target.value;
                            field.onChange(val);
                          }}
                        >
                          <MenuItem key={""} value={""}>
                            {t("labels.none")}
                          </MenuItem>
                          {plans.map((p) => (
                            <MenuItem
                              key={p.inventoryPlanId}
                              value={p.inventoryPlanId}
                            >
                              {p.companyName}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                  </EditingElement>
                ) : (
                  <ToEditElement field="plan">
                    <Typography variant="body1">
                      {selectedProduct.plan?.companyName}
                    </Typography>
                  </ToEditElement>
                )}
              </Grid>

              <Divider />

              <Grid item>
                <Grid container flexDirection="column">
                  <Typography variant="caption">
                    {t("inventory.created")} {createdAt}
                  </Typography>
                  <Typography variant="caption">
                    {t("inventory.lastModified")} {lastmodified}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            {/* </Grid> */}
          </form>
        </Grid>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            overflow: isPhoneSize ? "none" : "hidden",
            gap: 1,
          }}
        >
          <Typography variant="h6">{t("inventory.productHistory")}</Typography>
          <Box
            sx={{
              overflowY: "auto",
              overflowX: isPhoneSize ? "none" : "hidden",
            }}
          >
            <ProductTimeline history={history} />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ProductPopup;
